/* see docs/EXTRACTED_COMMENTS.md#c058 */

import type { API, BlockTune } from "@editorjs/editorjs"
import type { EditorDefaults, EditorDefaultsTuneConfig } from "./types"
import { toHex, toRgb } from "./colorUtils"

export class EditorDefaultsTune implements BlockTune {
  static isTune = true

  private config: EditorDefaultsTuneConfig

  constructor({
    config,
  }: {
    api: API
    config: EditorDefaultsTuneConfig
  }) {
    this.config = config
  }

  private current(): EditorDefaults {
    return this.config.getDefaults()
  }

  render(): HTMLElement {
    const panel = document.createElement("div")
    panel.className = "wdc-tune"

    const head = document.createElement("div")
    head.className = "wdc-tune__head"
    head.textContent = "Block defaults"
    panel.appendChild(head)

    panel.appendChild(
      this.field(
        "Text",
        this.colorControl(
          this.current().textColor,
          this.config.getForegroundOptions().map(o => ({
            color: o.color,
            className: o.className,
            label: o.label,
          })),
          (rgb, className) =>
            this.config.applyDefaults({
              textColor: rgb,
              foregroundClass: className,
            }),
          () =>
            this.config.applyDefaults({
              textColor: null,
              foregroundClass: null,
            }),
        ),
      ),
    )

    panel.appendChild(
      this.field(
        "Highlight",
        this.colorControl(
          this.current().backgroundColor,
          this.config.getBackgroundOptions().map(o => ({
            color: o.backgroundColor,
            className: o.className,
            label: o.label,
          })),
          (rgb, className) =>
            this.config.applyDefaults({
              backgroundColor: rgb,
              backgroundClass: className,
            }),
          () =>
            this.config.applyDefaults({
              backgroundColor: null,
              backgroundClass: null,
            }),
        ),
      ),
    )

    panel.appendChild(this.field("Font", this.fontSelect()))

    const sizeStyle = document.createElement("div")
    sizeStyle.className = "wdc-tune__split"
    sizeStyle.appendChild(this.field("Size", this.sizeSelect()))
    sizeStyle.appendChild(this.field("Style", this.styleToggles()))
    panel.appendChild(sizeStyle)

    return panel
  }

  save(): EditorDefaults {
    return this.current()
  }

  // --- panel building helpers -------------------------------------------

  private field(label: string, body: HTMLElement): HTMLElement {
    const wrapper = document.createElement("label")
    wrapper.className = "wdc-tune__field"

    const caption = document.createElement("span")
    caption.className = "wdc-tune__label"
    caption.textContent = label

    wrapper.appendChild(caption)
    wrapper.appendChild(body)
    return wrapper
  }

  private colorControl(
    seed: string | null,
    presets: Array<{ color: string; className: string; label: string }>,
    onPick: (rgb: string, className: string | null) => void,
    onClear: () => void,
  ): HTMLElement {
    const row = document.createElement("div")
    row.className = "wdc-tune__colors"

    const clear = document.createElement("button")
    clear.type = "button"
    clear.className = "wdc-tune__swatch wdc-tune__swatch--clear"
    clear.title = "None"
    clear.addEventListener("click", event => {
      event.preventDefault()
      onClear()
    })
    row.appendChild(clear)

    const custom = document.createElement("label")
    custom.className = "wdc-tune__custom"
    custom.title = "Custom colour"

    const input = document.createElement("input")
    input.type = "color"
    input.value = toHex(seed)
    custom.style.setProperty("--swatch", input.value)
    input.addEventListener("input", () => {
      custom.style.setProperty("--swatch", input.value)
    })
    input.addEventListener("change", () => {
      const rgb = toRgb(input.value)
      if (rgb) onPick(rgb, null)
    })
    custom.appendChild(input)
    row.appendChild(custom)

    for (const preset of presets) {
      const rgb = toRgb(preset.color) ?? preset.color
      const chip = document.createElement("button")
      chip.type = "button"
      chip.className = "wdc-tune__swatch"
      chip.style.setProperty("--swatch", rgb)
      chip.title = preset.label
      chip.addEventListener("click", event => {
        event.preventDefault()
        onPick(rgb, preset.className)
      })
      row.appendChild(chip)
    }

    return row
  }

  private fontSelect(): HTMLElement {
    const select = document.createElement("select")
    select.className = "wdc-tune__select"

    for (const option of this.config.getFontOptions()) {
      const opt = document.createElement("option")
      opt.value = option.className
      opt.textContent = option.label
      opt.dataset.fontFamily = option.fontFamily
      if (this.current().fontClass === option.className) opt.selected = true
      select.appendChild(opt)
    }

    select.addEventListener("change", () => {
      const opt = select.selectedOptions[0]
      if (!opt) return
      this.config.applyDefaults({
        fontClass: opt.value,
        fontFamily: opt.dataset.fontFamily ?? null,
      })
    })

    return select
  }

  private sizeSelect(): HTMLElement {
    const select = document.createElement("select")
    select.className = "wdc-tune__select"

    for (const size of this.config.getSizeOptions()) {
      const opt = document.createElement("option")
      opt.value = String(size)
      opt.textContent = `${size} pt`
      if (this.current().fontSize === size) opt.selected = true
      select.appendChild(opt)
    }

    select.addEventListener("change", () => {
      const size = Number.parseFloat(select.value)
      if (Number.isFinite(size) && size > 0) {
        this.config.applyDefaults({ fontSize: size })
      }
    })

    return select
  }

  private styleToggles(): HTMLElement {
    const group = document.createElement("div")
    group.className = "wdc-tune__segment"

    const makeToggle = (
      glyph: string,
      title: string,
      isActive: () => boolean,
      toggle: (active: boolean) => void,
      decorate: (el: HTMLElement) => void,
    ) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "wdc-tune__seg"
      button.textContent = glyph
      button.title = title
      decorate(button)
      if (isActive()) button.classList.add("is-active")
      button.addEventListener("click", event => {
        event.preventDefault()
        const active = isActive()
        toggle(active)
        button.classList.toggle("is-active", !active)
      })
      return button
    }

    group.appendChild(
      makeToggle(
        "B",
        "Bold",
        () => this.current().fontWeight === "bold",
        active =>
          this.config.applyDefaults({ fontWeight: active ? "normal" : "bold" }),
        el => (el.style.fontWeight = "700"),
      ),
    )
    group.appendChild(
      makeToggle(
        "I",
        "Italic",
        () => this.current().fontStyle === "italic",
        active =>
          this.config.applyDefaults({ fontStyle: active ? "normal" : "italic" }),
        el => (el.style.fontStyle = "italic"),
      ),
    )

    return group
  }
}
